/**
 * Shell/rail state shared by PnidWorkspaceShell (which owns the collapse
 * toggle, now that it lives on the frame bar) and PnidNavigationRail (which
 * renders against it). Kept out of both components so each module exports only
 * its component and React Fast Refresh keeps working.
 */

/** Collapse choice survives reloads; storage can throw in private windows. */
export const RAIL_COLLAPSED_KEY = 'pnid_rail_collapsed';

/* Collapsed is the DEFAULT: the rail is navigation, and the pages it leads to
   are drawings and tables that want the width. Only an explicit expand is
   remembered — an absent value means collapsed. */
export function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_COLLAPSED_KEY) !== '0';
  } catch {
    return true;
  }
}

export function storeCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* Session-only preference when storage is unavailable. */
  }
}

/** One label for the unscoped state, shared by the tile, the bar and the menu. */
export const ALL_PLANTS_LABEL = 'All Plants';

export function routeIsActive(currentPath: string, targetPath: string) {
  if (targetPath === '/drawings') return currentPath === '/drawings' || currentPath.startsWith('/pnid/');
  if (targetPath === '/work') return currentPath === '/work' || currentPath.startsWith('/packages/');
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

/* Deliberately asymmetric with accountLabel(), which does not title-case. */
export function tenantLabel(userGroup?: string) {
  const value = userGroup?.trim();
  if (!value) return 'Raven';
  if (value === 'sme_demo_group') return 'SME Demo';
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/* Pages that show one plant at a time and keep it in `domain_id`. Each one
   follows the plant scope in place, so a plant choice leaves the user there,
   and none of them has an unscoped view to offer. */
const SINGLE_PLANT_PAGES = ['/agent-review-queue', '/legend-sheets', '/plant-hierarchy'];

/** Whether the plant switcher offers "All Plants" on this page. */
export function plantSwitchOffersAllPlants(pathname: string): boolean {
  return !SINGLE_PLANT_PAGES.some((page) => routeIsActive(pathname, page));
}

/**
 * Where a plant choice from the rail lands, as a path with its query string.
 * The current location comes back when the page follows the scope in place;
 * the caller then has nothing to navigate.
 */
export function plantSwitchDestination(pathname: string, search: string): string {
  /* The library filters on the scope event, so it stays. Its `legend` names a
     legend of the old plant, and its `domain_id` makes a reload adopt the old
     plant again, so only the diagram type survives the switch. */
  if (pathname === '/drawings') {
    const type = new URLSearchParams(search).get('type');
    return type ? `/drawings?${new URLSearchParams({ type })}` : '/drawings';
  }
  /* The single-plant pages rewrite their own `domain_id` from the scope event,
     so they do not move. */
  if (SINGLE_PLANT_PAGES.includes(pathname)) return `${pathname}${search}`;
  /* A legend sheet record, or a new one, belongs to the plant it was opened
     under; the list lands on the new plant. */
  if (routeIsActive(pathname, '/legend-sheets')) return '/legend-sheets';
  /* A drawing belongs to its own plant, and every other page ignores the
     scope, so the choice opens that plant's drawings. */
  return '/drawings';
}
